<template>
  <div class="max-w-full mx-auto px-6 py-6 dark:bg-gray-600 dark:border-gray-600">
    <div class="mb-4 flex items-center justify-between">
      <h2 class="text-xl font-semibold text-gray-900 dark:text-gray-100">
        Ticket <span class="font-mono">#{{ $route.params.id }}</span>
      </h2>
      <div class="flex items-center gap-2">
        <button @click="$router.back()"
          class="rounded-md bg-gray-200 dark:bg-gray-500 px-3 py-1.5 text-sm text-gray-800 dark:text-gray-100 hover:bg-gray-300 dark:hover:bg-gray-400">&larr;
          Back</button>
      </div>
    </div>

    <div v-if="loading"></div>
    <div v-else-if="!ticket" class="py-10 text-center text-sm text-red-500">Ticket not found.</div>

    <div v-else class="space-y-6">
      <section class="rounded-md border border-gray-200 dark:border-gray-500 bg-white dark:bg-gray-700 p-4">
        <div class="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
          <Field label="Company" :value="ticket.company_name" />
          <Field label="Tech" :value="ticket.tech_name" />
          <Field label="Status" :value="statusLabel" />
          <Field label="Type" :value="ticket.ticket_type_name" />
          <Field label="Submitted" :value="formatDate(ticket.submitted)" />
          <Field label="Completed" :value="formatDate(ticket.completed)" />
          <Field label="Requester" :value="ticket.requester" />
          <Field label="Email" :value="ticket.email" />
        </div>
      </section>

      <TextBlock label="Description" :value="ticket.description" />
      <TextBlock label="Delegator Notes" :value="ticket.delegatorNotes" />
      <TextBlock label="Final Note" :value="ticket.finalNote" />
      <TextBlock label="Private Info" :value="ticket.privateinfo" />
      <TextBlock label="Pending Reason" :value="ticket.pending_reason" />

      <section>
        <h3 class="mb-2 text-lg font-semibold text-gray-900 dark:text-gray-100">
          Jobs ({{ (ticket.jobs || []).length }})
        </h3>
        <div v-if="!ticket.jobs || !ticket.jobs.length" class="text-sm text-gray-500 dark:text-gray-300">No jobs
          recorded for this ticket.</div>
        <div v-else class="overflow-x-auto rounded-md border border-gray-200 dark:border-gray-500">
          <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-500 text-sm">
            <thead class="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th class="px-3 py-2 text-left font-medium text-gray-700 dark:text-gray-100">Job ID</th>
                <th class="px-3 py-2 text-left font-medium text-gray-700 dark:text-gray-100">Date</th>
                <th class="px-3 py-2 text-left font-medium text-gray-700 dark:text-gray-100">Tech</th>
                <th class="px-3 py-2 text-right font-medium text-gray-700 dark:text-gray-100">Hours</th>
                <th class="px-3 py-2 text-left font-medium text-gray-700 dark:text-gray-100">Billed</th>
                <th class="px-3 py-2 text-left font-medium text-gray-700 dark:text-gray-100">Description</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-200 dark:divide-gray-500 bg-white dark:bg-gray-700">
              <tr v-for="j in ticket.jobs" :key="j.id" class="hover:bg-yellow-50 dark:hover:bg-gray-600 cursor-pointer"
                @click="$router.push({ name: 'reportingJobDetail', params: { id: j.id } })">
                <td class="px-3 py-2 font-mono text-xs text-gray-800 dark:text-gray-100">{{ j.id }}</td>
                <td class="px-3 py-2 whitespace-nowrap text-gray-800 dark:text-gray-100">{{ formatDate(j.jdate) }}</td>
                <td class="px-3 py-2 whitespace-nowrap text-gray-800 dark:text-gray-100">{{ j.tech_name || '—' }}</td>
                <td class="px-3 py-2 whitespace-nowrap text-right text-gray-800 dark:text-gray-100">{{
                  formatHours(j.totalTime) }}</td>
                <td class="px-3 py-2 whitespace-nowrap text-gray-800 dark:text-gray-100">{{ j.billed || '—' }}</td>
                <td class="px-3 py-2 max-w-md text-gray-800 dark:text-gray-100">
                  <div class="line-clamp-3" v-html="renderMarkdown(j.description, { compact: true, maxLines: 6 })"></div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </div>
  </div>
</template>

<script setup>
import { renderMarkdown } from '@/utils/markdown';
import { ref, computed, onMounted, watch, getCurrentInstance } from 'vue';
import { useRoute, RouterLink } from 'vue-router';
import axios from 'axios';
import Field from './_Field.vue';
import TextBlock from './_TextBlock.vue';

const route = useRoute();
const ticket = ref(null);
const loading = ref(true);
const { proxy } = getCurrentInstance();
const updateLoading = (n) => proxy?.$updateLoading?.(n);

const statusLabel = computed(() => {
  if (!ticket.value) return '';
  const c = ticket.value.completed;
  return (!c || String(c).startsWith('0000')) ? 'Open' : 'Closed';
});

async function load(id) {
  loading.value = true;
  updateLoading(1);
  try {
    const { data } = await axios.get(`reporting/tickets/${id}`);
    ticket.value = data;
  } catch (e) { ticket.value = null; }
  finally { loading.value = false; updateLoading(-1); }
}

function formatDate(d) {
  if (!d || String(d).startsWith('0000')) return '—';
  return String(d).substring(0, 19);
}
function formatHours(h) {
  if (h === null || h === undefined) return '—';
  return Number(h).toFixed(2);
}
watch(() => route.params.id, (id) => id && load(id));
onMounted(() => load(route.params.id));
</script>
